import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DateTimeRangePicker,
  DateTimeRange,
} from '@/components/common/date-pickers/date-time-range-picker';
import {
  useBulkRetriggerEmailRules,
  usePreviewBulkRetriggerEmailRules,
} from '@/api/communications/useBulkRetriggerEmailRules';
import { useEmailRules } from '@/api/rpc/hooks/communications/useEmailRules';
import { CircleNotchIcon } from '@phosphor-icons/react';

interface BulkRetriggerEmailRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkRetriggerEmailRulesDialog({
  open,
  onOpenChange,
}: BulkRetriggerEmailRulesDialogProps) {
  const [dateRange, setDateRange] = useState<DateTimeRange | undefined>(
    undefined,
  );
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);

  const bulkRetriggerMutation = useBulkRetriggerEmailRules();
  const { data: rulesData, isLoading: isLoadingRules } = useEmailRules();

  // Only fetch preview when we have a valid date range
  const previewInput = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return null;
    }
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      ruleIds: selectedRuleIds.length > 0 ? selectedRuleIds : undefined,
    };
  }, [dateRange, selectedRuleIds]);

  const { data: previewData, isFetching: isFetchingPreview } =
    usePreviewBulkRetriggerEmailRules(previewInput);

  const enabledRules = useMemo(() => {
    return rulesData?.rules.filter((rule) => rule.enabled) ?? [];
  }, [rulesData]);

  const handleRuleToggle = (ruleId: string, checked: boolean) => {
    if (checked) {
      setSelectedRuleIds((prev) => [...prev, ruleId]);
    } else {
      setSelectedRuleIds((prev) => prev.filter((id) => id !== ruleId));
    }
  };

  const handleSelectAll = () => {
    if (selectedRuleIds.length === enabledRules.length) {
      setSelectedRuleIds([]);
    } else {
      setSelectedRuleIds(enabledRules.map((rule) => rule.id));
    }
  };

  const handleSubmit = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    await bulkRetriggerMutation.mutateAsync({
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      ruleIds: selectedRuleIds.length > 0 ? selectedRuleIds : undefined,
    });

    // Close dialog and reset state on success
    setDateRange(undefined);
    setSelectedRuleIds([]);
    onOpenChange(false);
  };

  const handleClose = () => {
    setDateRange(undefined);
    setSelectedRuleIds([]);
    onOpenChange(false);
  };

  const isValid = dateRange?.from && dateRange?.to;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Retrigger Email Rules</DialogTitle>
          <DialogDescription>
            Select a date range and optionally filter by specific email rules.
            Only emails matching the selected rules will be retriggered.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="mb-2 block">Date Range</Label>
            <DateTimeRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder="Select date range"
              className="w-full"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Email Rules (Optional)</Label>
              {enabledRules.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-auto py-1 px-2 text-xs"
                >
                  {selectedRuleIds.length === enabledRules.length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Leave unselected to retrigger all emails, or select specific rules
              to only retrigger emails that match those rules.
            </p>
            {isLoadingRules ? (
              <div className="flex items-center justify-center py-4">
                <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : enabledRules.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No enabled email rules found.
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-3">
                {enabledRules.map((rule) => (
                  <div key={rule.id} className="flex items-start space-x-2">
                    <Checkbox
                      id={`rule-${rule.id}`}
                      checked={selectedRuleIds.includes(rule.id)}
                      onCheckedChange={(checked) =>
                        handleRuleToggle(rule.id, checked === true)
                      }
                    />
                    <div className="grid gap-0.5 leading-none">
                      <Label
                        htmlFor={`rule-${rule.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {rule.ruleName}
                      </Label>
                      {rule.filters && Object.keys(rule.filters).length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {formatRuleFilters(rule.filters)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isValid && (
            <div className="bg-muted/50 rounded-md p-3">
              <div className="flex items-center gap-2">
                {isFetchingPreview ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Calculating...
                    </span>
                  </>
                ) : (
                  <span className="text-sm">
                    <span className="font-semibold">
                      {previewData?.emailCount ?? 0}
                    </span>{' '}
                    email{previewData?.emailCount === 1 ? '' : 's'} will be
                    retriggered
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !isValid ||
              bulkRetriggerMutation.isPending ||
              previewData?.emailCount === 0
            }
          >
            {bulkRetriggerMutation.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Retrigger Email Rules
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatRuleFilters(filters: {
  from?: string[];
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string[];
  mailbox?: string[];
}): string {
  const parts: string[] = [];

  if (filters.from?.length) {
    parts.push(`From: ${filters.from.join(', ')}`);
  }
  if (filters.to?.length) {
    parts.push(`To: ${filters.to.join(', ')}`);
  }
  if (filters.cc?.length) {
    parts.push(`CC: ${filters.cc.join(', ')}`);
  }
  if (filters.subject?.length) {
    parts.push(`Subject: ${filters.subject.join(', ')}`);
  }
  if (filters.mailbox?.length) {
    parts.push(`Mailbox: ${filters.mailbox.join(', ')}`);
  }

  return parts.join(' | ') || 'No filters';
}
